import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  listOfferInvites as listOfferInvitesService,
  createOfferInvite as createOfferInviteService,
  getOfferInvite as getOfferInviteService,
  patchOfferInvite as patchOfferInviteService,
} from "../services/invitesService";

const norm = (v) => (v == null ? "" : String(v).trim());

const initialOfferBucket = () => ({
  items: [],
  nextCursor: null,
  hasMore: false,
  loading: false,
  error: null,
  statusSummary: {},
  currentStatusSummary: {},
});

const initialState = {
  byOffer: {},
  creating: false,
  createError: null,
  fetchingOne: false,
  fetchOneError: null,
  current: null,
  updating: false,
  updateError: null,
};

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err?.message || fallback;
};

const getInviteId = (invite) =>
  norm(invite?.inviteId || invite?.id || invite?._id || "");

const ensureBucket = (state, offerId) => {
  if (!offerId) return null;
  if (!state.byOffer[offerId]) {
    state.byOffer[offerId] = initialOfferBucket();
  }
  return state.byOffer[offerId];
};

function unwrapResponseData(resp) {
  if (
    resp &&
    typeof resp === "object" &&
    resp.data &&
    typeof resp.data === "object"
  ) {
    return resp.data;
  }
  return resp;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = norm(value);
    if (text) return text;
  }
  return "";
}

function normalizeApiStatus(value) {
  const raw = norm(value).toLowerCase();

  if (!raw) return "draft";

  if (["draft", "pending", "new"].includes(raw)) return "draft";
  if (["sent", "shared", "delivered"].includes(raw)) return "sent";
  if (["accepted", "approved"].includes(raw)) return "accepted";
  if (["live", "posted", "published"].includes(raw)) return "live";
  if (["completed", "complete", "fulfilled", "closed"].includes(raw)) {
    return "completed";
  }
  if (["declined", "rejected", "cancelled", "canceled"].includes(raw)) {
    return "declined";
  }

  return raw;
}

function getMilestoneValue(invite, key, fallbackKeys = []) {
  const milestones =
    invite?.milestones && typeof invite.milestones === "object"
      ? invite.milestones
      : {};

  const direct = norm(invite?.[key]);
  if (direct) return direct;

  const nested = norm(milestones?.[key]);
  if (nested) return nested;

  for (const fallbackKey of fallbackKeys) {
    const fromDirect = norm(invite?.[fallbackKey]);
    if (fromDirect) return fromDirect;

    const fromNested = norm(milestones?.[fallbackKey]);
    if (fromNested) return fromNested;
  }

  return "";
}

function getLinkValue(invite, key, fallbackFields = []) {
  const links =
    invite?.links && typeof invite.links === "object" ? invite.links : {};

  const nested = norm(links?.[key]);
  if (nested) return nested;

  for (const field of fallbackFields) {
    const value = norm(invite?.[field]);
    if (value) return value;
  }

  return "";
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return [];

  const seen = new Set();
  const out = [];

  for (const item of value) {
    const text = norm(item);
    if (!text || seen.has(text)) continue;
    seen.add(text);
    out.push(text);
  }

  return out;
}

function normalizeCountMap(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const out = {};

  for (const [key, raw] of Object.entries(value)) {
    const cleanKey = norm(key);
    if (!cleanKey) continue;

    const n = Number(raw);
    out[cleanKey] = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  }

  return out;
}

function buildStatusSummary(items = [], field = "status") {
  const summary = {};

  for (const item of Array.isArray(items) ? items : []) {
    const status = normalizeApiStatus(item?.[field]);
    if (!status) continue;
    summary[status] = (summary[status] || 0) + 1;
  }

  return summary;
}

function recomputeBucketSummaries(bucket) {
  if (!bucket) return;

  const items = Array.isArray(bucket.items) ? bucket.items : [];
  bucket.statusSummary = normalizeCountMap(buildStatusSummary(items, "status"));
  bucket.currentStatusSummary = normalizeCountMap(
    buildStatusSummary(items, "currentStatus")
  );
}

function normalizeInvite(invite) {
  if (!invite || typeof invite !== "object") return invite;

  const inviteId = firstNonEmpty(invite?.inviteId, invite?.id, invite?._id);

  const status = normalizeApiStatus(
    invite?.status ||
      invite?.inviteStatus ||
      invite?.state ||
      invite?.lifecycleStatus ||
      invite?.statusLabel
  );

  const currentStatus = normalizeApiStatus(
    invite?.currentStatus ||
      invite?.current_status ||
      invite?.status ||
      invite?.inviteStatus ||
      invite?.statusLabel
  );

  const handle = firstNonEmpty(
    invite?.handle,
    invite?.username,
    invite?.creatorHandle,
    invite?.influencerHandle,
    invite?.creator?.username,
    invite?.creator?.handle
  ).replace(/^@+/, "");

  const creatorName = firstNonEmpty(
    invite?.creatorName,
    invite?.full_name,
    invite?.name,
    invite?.creator?.full_name,
    invite?.creator?.name
  );

  const sentAt = getMilestoneValue(invite, "sentAt", [
    "sharedAt",
    "deliveredAt",
    "sent_at",
    "shared_at",
    "delivered_at",
  ]);

  const acceptedAt = getMilestoneValue(invite, "acceptedAt", [
    "accepted_at",
    "acceptedOn",
    "accepted_on",
  ]);

  const liveAt = getMilestoneValue(invite, "liveAt", [
    "live_at",
    "wentLiveAt",
    "went_live_at",
    "postedAt",
    "posted_at",
  ]);

  const completedAt = getMilestoneValue(invite, "completedAt", [
    "completed_at",
    "closedAt",
    "closed_at",
    "fulfilledAt",
    "fulfilled_at",
  ]);

  const declinedAt = getMilestoneValue(invite, "declinedAt", [
    "declined_at",
    "rejectedAt",
    "rejected_at",
  ]);

  const trackingUrl = getLinkValue(invite, "tracking", [
    "trackingUrl",
    "tracking_url",
    "trackingLink",
    "redirectUrl",
    "redirect_url",
    "linkUrl",
    "link_url",
    "shortUrl",
    "short_url",
  ]);

  const publicInviteUrl = getLinkValue(invite, "publicInvite", [
    "publicInviteUrl",
    "publicInviteURL",
    "public_invite_url",
    "public_url",
    "publicUrl",
    "inviteUrl",
    "invite_url",
    "shareUrl",
    "share_url",
  ]);

  const creatorOnboardingLink = getLinkValue(invite, "creatorOnboarding", [
    "creatorOnboardingLink",
    "creator_onboarding_link",
    "onboardingLink",
    "onboarding_url",
  ]);

  const dmText = firstNonEmpty(
    invite?.dmText,
    invite?.dm_text,
    invite?.message,
    invite?.outreachText,
    invite?.outreach_text
  );

  const shared =
    invite?.shared === true ||
    Boolean(sentAt) ||
    Boolean(firstNonEmpty(invite?.sharedAt, invite?.shared_at)) ||
    ["sent", "accepted", "live", "completed", "declined"].includes(
      currentStatus
    );

  return {
    ...invite,
    inviteId: inviteId || undefined,
    id: firstNonEmpty(invite?.id, inviteId) || undefined,
    offerId: firstNonEmpty(invite?.offerId, invite?.offer_id) || "",
    campaignId:
      firstNonEmpty(invite?.campaignId, invite?.campaign_id) || null,
    creatorId:
      firstNonEmpty(
        invite?.creatorId,
        invite?.creator_id,
        invite?.influencerId,
        invite?.influencer_id
      ) || null,
    influencerId:
      firstNonEmpty(
        invite?.influencerId,
        invite?.influencer_id,
        invite?.creatorId,
        invite?.creator_id
      ) || null,
    status: status || "draft",
    currentStatus: currentStatus || status || "draft",
    handle: handle || invite?.handle || "",
    creatorName: creatorName || invite?.creatorName || "",
    shared,
    sharedAt: firstNonEmpty(invite?.sharedAt, invite?.shared_at, sentAt) || "",
    sentAt: sentAt || "",
    acceptedAt: acceptedAt || "",
    liveAt: liveAt || "",
    completedAt: completedAt || "",
    declinedAt: declinedAt || "",
    publicInviteUrl: publicInviteUrl || "",
    trackingUrl: trackingUrl || "",
    trackingLink: trackingUrl || "",
    creatorOnboardingLink: creatorOnboardingLink || "",
    dmText: dmText || "",
    onboardingStatus: firstNonEmpty(
      invite?.onboardingStatus,
      invite?.onboarding_status
    ),
    onboardingSubmittedAt: firstNonEmpty(
      invite?.onboardingSubmittedAt,
      invite?.onboarding_submitted_at
    ),
    onboarding:
      invite?.onboarding && typeof invite.onboarding === "object"
        ? invite.onboarding
        : invite?.details && typeof invite.details === "object"
          ? invite.details
          : null,
    availableActions: normalizeStringList(invite?.availableActions),
    links: {
      ...(invite?.links && typeof invite.links === "object" ? invite.links : {}),
      tracking: trackingUrl || "",
      publicInvite: publicInviteUrl || "",
      creatorOnboarding: creatorOnboardingLink || "",
    },
    milestones: {
      ...(invite?.milestones && typeof invite.milestones === "object"
        ? invite.milestones
        : {}),
      sentAt: sentAt || "",
      acceptedAt: acceptedAt || "",
      liveAt: liveAt || "",
      completedAt: completedAt || "",
      declinedAt: declinedAt || "",
    },
  };
}

function normalizeInviteArray(items) {
  return (Array.isArray(items) ? items : [])
    .map(normalizeInvite)
    .filter(Boolean);
}

function dedupeById(items = []) {
  const seen = new Set();
  const out = [];

  for (const rawItem of Array.isArray(items) ? items : []) {
    const item = normalizeInvite(rawItem);
    const id = getInviteId(item) || JSON.stringify(item);

    if (seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }

  return out;
}

function mergeInvite(current = {}, incoming = {}) {
  const merged = {
    ...(current || {}),
    ...(incoming || {}),
  };
  return normalizeInvite(merged);
}

function upsertInviteInList(items = [], incoming) {
  const normalizedIncoming = normalizeInvite(incoming);

  if (!normalizedIncoming || typeof normalizedIncoming !== "object") {
    return Array.isArray(items) ? items : [];
  }

  const incomingId = getInviteId(normalizedIncoming);

  if (!incomingId) {
    return dedupeById([
      normalizedIncoming,
      ...(Array.isArray(items) ? items : []),
    ]);
  }

  let found = false;

  const next = (Array.isArray(items) ? items : []).map((item) => {
    if (getInviteId(item) === incomingId) {
      found = true;
      return mergeInvite(item, normalizedIncoming);
    }
    return normalizeInvite(item);
  });

  if (!found) {
    next.unshift(normalizedIncoming);
  }

  return dedupeById(next);
}

function parseListResponse(resp) {
  const body = unwrapResponseData(resp);

  if (Array.isArray(body)) {
    return {
      items: normalizeInviteArray(body),
      nextCursor: null,
      statusSummary: {},
      currentStatusSummary: {},
    };
  }

  if (body && typeof body === "object") {
    const items =
      body.items ||
      body.invites ||
      body.results ||
      body.data?.items ||
      body.data?.invites ||
      [];

    return {
      items: normalizeInviteArray(items),
      nextCursor:
        body.next_cursor ??
        body.nextCursor ??
        body.cursor ??
        body.last_key ??
        body.lastKey ??
        body.data?.next_cursor ??
        body.data?.nextCursor ??
        null,
      statusSummary: normalizeCountMap(
        body.statusSummary ?? body.status_summary ?? body.data?.statusSummary
      ),
      currentStatusSummary: normalizeCountMap(
        body.currentStatusSummary ??
          body.current_status_summary ??
          body.data?.currentStatusSummary
      ),
    };
  }

  return {
    items: [],
    nextCursor: null,
    statusSummary: {},
    currentStatusSummary: {},
  };
}

function parseSingleInviteResponse(resp) {
  const body = unwrapResponseData(resp);
  if (!body || typeof body !== "object") return null;

  const invite = body.created || body.updated || body.invite || body.item || body;
  return normalizeInvite(invite);
}

export const fetchCreatorInvitesAsync = createAsyncThunk(
  "creatorInvites/listByOffer",
  async ({ offerId, params = {}, append = false }, { rejectWithValue }) => {
    try {
      if (!offerId) {
        throw new Error("offerId is required to fetch invites.");
      }

      const resp = await listOfferInvitesService(offerId, params);
      const parsed = parseListResponse(resp);

      return {
        offerId,
        items: parsed.items,
        nextCursor: parsed.nextCursor,
        statusSummary: parsed.statusSummary,
        currentStatusSummary: parsed.currentStatusSummary,
        append,
      };
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, "Failed to load invites."));
    }
  }
);

export const createCreatorInviteAsync = createAsyncThunk(
  "creatorInvites/create",
  async ({ offerId, payload }, { rejectWithValue }) => {
    try {
      if (!offerId) {
        throw new Error("offerId is required to create an invite.");
      }

      const resp = await createOfferInviteService(offerId, payload);
      const created = parseSingleInviteResponse(resp);

      return {
        offerId,
        created,
      };
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, "Failed to create invite."));
    }
  }
);

export const getCreatorInviteAsync = createAsyncThunk(
  "creatorInvites/getOne",
  async ({ offerId, inviteId }, { rejectWithValue }) => {
    try {
      if (!offerId) {
        throw new Error("offerId is required.");
      }
      if (!inviteId) {
        throw new Error("inviteId is required.");
      }

      const resp = await getOfferInviteService(offerId, inviteId);
      const invite = parseSingleInviteResponse(resp);

      return {
        offerId,
        invite,
      };
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, "Failed to load invite."));
    }
  }
);

export const patchCreatorInviteAsync = createAsyncThunk(
  "creatorInvites/patch",
  async ({ offerId, inviteId, payload }, { rejectWithValue }) => {
    try {
      if (!offerId) {
        throw new Error("offerId is required.");
      }
      if (!inviteId) {
        throw new Error("inviteId is required.");
      }

      const resp = await patchOfferInviteService(offerId, inviteId, payload);
      const updated = parseSingleInviteResponse(resp);

      return {
        offerId,
        updated,
      };
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, "Failed to update invite."));
    }
  }
);

const creatorInviteSlice = createSlice({
  name: "creatorInvites",
  initialState,
  reducers: {
    clearCreatorInviteErrors(state) {
      Object.values(state.byOffer).forEach((bucket) => {
        if (bucket) bucket.error = null;
      });

      state.createError = null;
      state.fetchOneError = null;
      state.updateError = null;
    },

    resetCreatorInvitesState() {
      return {
        byOffer: {},
        creating: false,
        createError: null,
        fetchingOne: false,
        fetchOneError: null,
        current: null,
        updating: false,
        updateError: null,
      };
    },

    clearCurrentInvite(state) {
      state.current = null;
      state.fetchingOne = false;
      state.fetchOneError = null;
    },

    resetOfferInvites(state, action) {
      const offerId = action.payload;
      if (!offerId) return;
      state.byOffer[offerId] = initialOfferBucket();
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchCreatorInvitesAsync.pending, (state, action) => {
        const offerId = action.meta.arg?.offerId;
        const bucket = ensureBucket(state, offerId);
        if (!bucket) return;

        bucket.loading = true;
        bucket.error = null;
      })
      .addCase(fetchCreatorInvitesAsync.fulfilled, (state, action) => {
        const {
          offerId,
          items,
          nextCursor,
          statusSummary,
          currentStatusSummary,
          append,
        } = action.payload || {};
        const bucket = ensureBucket(state, offerId);
        if (!bucket) return;

        bucket.loading = false;

        const merged = append
          ? [...bucket.items, ...(Array.isArray(items) ? items : [])]
          : Array.isArray(items)
            ? items
            : [];

        bucket.items = dedupeById(merged);
        bucket.nextCursor = nextCursor || null;
        bucket.hasMore = Boolean(nextCursor);

        bucket.statusSummary =
          Object.keys(normalizeCountMap(statusSummary)).length > 0
            ? normalizeCountMap(statusSummary)
            : normalizeCountMap(buildStatusSummary(bucket.items, "status"));

        bucket.currentStatusSummary =
          Object.keys(normalizeCountMap(currentStatusSummary)).length > 0
            ? normalizeCountMap(currentStatusSummary)
            : normalizeCountMap(buildStatusSummary(bucket.items, "currentStatus"));
      })
      .addCase(fetchCreatorInvitesAsync.rejected, (state, action) => {
        const offerId = action.meta.arg?.offerId;
        const message = action.payload || "Failed to load invites.";

        if (!offerId) {
          state.createError = message;
          return;
        }

        const bucket = ensureBucket(state, offerId);
        if (!bucket) return;

        bucket.loading = false;
        bucket.error = message;
      })

      .addCase(createCreatorInviteAsync.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createCreatorInviteAsync.fulfilled, (state, action) => {
        state.creating = false;

        const { offerId, created } = action.payload || {};
        const bucket = ensureBucket(state, offerId);
        if (!bucket || !created) return;

        bucket.items = upsertInviteInList(bucket.items, created);
        recomputeBucketSummaries(bucket);

        if (getInviteId(state.current) === getInviteId(created)) {
          state.current = mergeInvite(state.current, created);
        }
      })
      .addCase(createCreatorInviteAsync.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.payload || "Failed to create invite.";
      })

      .addCase(getCreatorInviteAsync.pending, (state) => {
        state.fetchingOne = true;
        state.fetchOneError = null;
      })
      .addCase(getCreatorInviteAsync.fulfilled, (state, action) => {
        state.fetchingOne = false;

        const { offerId, invite } = action.payload || {};
        state.current = invite || null;

        if (offerId && invite) {
          const bucket = ensureBucket(state, offerId);
          if (bucket) {
            bucket.items = upsertInviteInList(bucket.items, invite);
            recomputeBucketSummaries(bucket);
          }
        }
      })
      .addCase(getCreatorInviteAsync.rejected, (state, action) => {
        state.fetchingOne = false;
        state.fetchOneError = action.payload || "Failed to load invite.";
      })

      .addCase(patchCreatorInviteAsync.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(patchCreatorInviteAsync.fulfilled, (state, action) => {
        state.updating = false;

        const { offerId, updated } = action.payload || {};
        const bucket = ensureBucket(state, offerId);
        if (!bucket || !updated) return;

        bucket.items = upsertInviteInList(bucket.items, updated);
        recomputeBucketSummaries(bucket);

        if (state.current && getInviteId(state.current) === getInviteId(updated)) {
          state.current = mergeInvite(state.current, updated);
        }
      })
      .addCase(patchCreatorInviteAsync.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || "Failed to update invite.";
      });
  },
});

export const {
  clearCreatorInviteErrors,
  resetCreatorInvitesState,
  clearCurrentInvite,
  resetOfferInvites,
} = creatorInviteSlice.actions;

export const selectInvitesBucket = (state, offerId) =>
  state.invites?.byOffer?.[offerId] || initialOfferBucket();

export const selectCreatorInvites = (state, offerId) =>
  selectInvitesBucket(state, offerId).items;

export const selectCreatorInvitesLoading = (state, offerId) =>
  selectInvitesBucket(state, offerId).loading;

export const selectCreatorInvitesError = (state, offerId) =>
  selectInvitesBucket(state, offerId).error;

export const selectCreatorInvitesLastKey = (state, offerId) =>
  selectInvitesBucket(state, offerId).nextCursor;

export const selectCreatorInvitesHasMore = (state, offerId) =>
  selectInvitesBucket(state, offerId).hasMore;

export const selectCreatorInvitesStatusSummary = (state, offerId) =>
  selectInvitesBucket(state, offerId).statusSummary;

export const selectCreatorInvitesCurrentStatusSummary = (state, offerId) =>
  selectInvitesBucket(state, offerId).currentStatusSummary;

export const selectCreatorInviteCreating = (state) =>
  state.invites?.creating || false;

export const selectCreatorInviteCreateError = (state) =>
  state.invites?.createError || null;

export const selectCurrentInvite = (state) =>
  state.invites?.current || null;

export const selectCurrentInviteLoading = (state) =>
  state.invites?.fetchingOne || false;

export const selectCurrentInviteError = (state) =>
  state.invites?.fetchOneError || null;

export const selectInviteUpdating = (state) =>
  state.invites?.updating || false;

export const selectInviteUpdateError = (state) =>
  state.invites?.updateError || null;

export default creatorInviteSlice.reducer;